import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './ParentReviews.css';

/* =========================================================
   Homepage › What parents say
   Only real reviews that the admin turned on (Admin › Parent feedback).
   - a slider: 3 cards on desktop, 2 on tablet, 1 on phones (the next one peeks in),
     swipe or arrows, moves by itself every 6 s (stops while the visitor touches / hovers it)
   - comments show 3 lines at most, with "Read more" when there is more
   Shows nothing when there are no reviews.
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const AUTO_MS = 6000;

const Stars = ({ value }) => (
  <span className="rmprv-stars" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={n <= Math.round(value) ? 'is-on' : ''} aria-hidden="true">★</span>
    ))}
  </span>
);

const Card = ({ r }) => {
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false); // more than 3 lines?
  const quoteRef = useRef(null);
  const text = String(r.Liked || '');
  const name = r.DisplayName || 'A RoboMind parent';

  // measure while cut to 3 lines (again when the screen size changes)
  useEffect(() => {
    const check = () => {
      const el = quoteRef.current;
      if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text, open]);

  return (
    <li className="rmprv-card">
      <Stars value={r.RatingAcademy} />
      <blockquote dir="auto" ref={quoteRef} className={open ? '' : 'is-cut'}>
        {text}
      </blockquote>
      {long && (
        <button type="button" className="rmprv-more" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
      <p className="rmprv-who">
        <span className="rmprv-avatar" aria-hidden="true">
          {name.replace(/^Parent of /i, '').charAt(0).toUpperCase()}
        </span>
        <span dir="auto">{name}</span>
      </p>
    </li>
  );
};

const ParentReviews = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0); // first card in view
  const [pages, setPages] = useState(1); // how many "stops" the slider has
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/feedback/testimonials`)
      .then((res) => {
        if (!alive) return;
        setItems(res.data?.items || []);
      })
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, []);

  // which card is first in view, and how many stops there are
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const step = track.children[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
    if (!step) return;
    const perView = Math.max(1, Math.round(track.clientWidth / step));
    setPages(Math.max(1, items.length - perView + 1));
    setActive(Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
  }, [items.length]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const goTo = useCallback(
    (i) => {
      const track = trackRef.current;
      if (!track || !track.children.length) return;
      const target = track.children[Math.max(0, Math.min(i, items.length - 1))];
      track.scrollTo({ left: target.offsetLeft - track.children[0].offsetLeft, behavior: 'smooth' });
    },
    [items.length]
  );

  // move by itself (not when the visitor prefers less motion, or is touching / hovering it)
  useEffect(() => {
    if (pages <= 1) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => {
      if (pausedRef.current || document.visibilityState !== 'visible') return;
      goTo(active + 1 >= pages ? 0 : active + 1);
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [active, pages, goTo]);

  if (!items.length) return null;

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };

  return (
    <section className="rmprv" aria-labelledby="rmprv-title">
      <div className="rmprv-wrap">
        <p className="rmprv-kicker">Real parents · Real feedback</p>
        <h2 id="rmprv-title" className="rmprv-title">What parents say about RoboMind</h2>

        <div
          className="rmprv-slider"
          onMouseEnter={pause}
          onMouseLeave={resume}
          onTouchStart={pause}
          onTouchEnd={() => setTimeout(resume, 4000)}
          onFocus={pause}
          onBlur={resume}
        >
          {pages > 1 && (
            <button type="button" className="rmprv-arrow is-prev" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous review">
              ‹
            </button>
          )}
          <ul className={`rmprv-track ${items.length < 3 ? 'is-few' : ''}`} ref={trackRef} onScroll={measure}>
            {items.map((r) => (
              <Card key={r.Id} r={r} />
            ))}
          </ul>
          {pages > 1 && (
            <button type="button" className="rmprv-arrow is-next" onClick={() => goTo(active + 1)} disabled={active >= pages - 1} aria-label="Next review">
              ›
            </button>
          )}
        </div>

        {pages > 1 && (
          <div className="rmprv-dots" role="tablist" aria-label="Choose a review">
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`Review ${i + 1}`}
                className={i === active ? 'is-on' : ''}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ParentReviews;
